/* Cryo Games landing page (templates/page.cryo-games.json) */

if (!customElements.get('cg-countdown')) {
  // Counts down to data-end (any date string Date() can parse, e.g. an ISO date).
  // Stays at zeros when no end date is set or it has passed.
  customElements.define(
    'cg-countdown',
    class CgCountdown extends HTMLElement {
      connectedCallback() {
        this.end = new Date(this.dataset.end).getTime();
        this.fields = {
          days: this.querySelector('[data-unit="days"]'),
          hours: this.querySelector('[data-unit="hours"]'),
          minutes: this.querySelector('[data-unit="minutes"]'),
          seconds: this.querySelector('[data-unit="seconds"]'),
        };
        if (Number.isNaN(this.end)) return;
        this.update();
        this.interval = setInterval(() => this.update(), 1000);
      }

      disconnectedCallback() {
        clearInterval(this.interval);
      }

      update() {
        const distance = Math.max(0, this.end - Date.now());
        const second = 1000;
        const minute = second * 60;
        const hour = minute * 60;
        const day = hour * 24;
        const values = {
          days: Math.floor(distance / day),
          hours: Math.floor((distance % day) / hour),
          minutes: Math.floor((distance % hour) / minute),
          seconds: Math.floor((distance % minute) / second),
        };
        Object.entries(values).forEach(([unit, value]) => {
          if (this.fields[unit]) this.fields[unit].textContent = String(value).padStart(2, '0');
        });
        if (distance === 0) clearInterval(this.interval);
      }
    }
  );
}

if (!customElements.get('cg-slider')) {
  // Scroll-snap slider with optional prev/next arrows and page dots.
  customElements.define(
    'cg-slider',
    class CgSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.cg-slider__track');
        this.slides = Array.from(this.track.children);
        this.prev = this.querySelector('.cg-slider__arrow--prev');
        this.next = this.querySelector('.cg-slider__arrow--next');
        this.dots = this.querySelector('.cg-slider__dots');

        this.prev?.addEventListener('click', () => this.go(this.page - 1));
        this.next?.addEventListener('click', () => this.go(this.page + 1));
        this.track.addEventListener('scroll', () => this.sync(), { passive: true });
        this.resizeObserver = new ResizeObserver(() => this.build());
        this.resizeObserver.observe(this.track);
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
      }

      get perPage() {
        const slide = this.slides[0];
        if (!slide) return 1;
        return Math.max(1, Math.round(this.track.clientWidth / slide.getBoundingClientRect().width));
      }

      get pageCount() {
        return Math.max(1, Math.ceil(this.slides.length / this.perPage));
      }

      get page() {
        const slideWidth = this.slides[0]?.offsetWidth || 1;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const index = Math.round(this.track.scrollLeft / (slideWidth + gap));
        return Math.min(this.pageCount - 1, Math.round(index / this.perPage));
      }

      build() {
        if (!this.dots) return this.sync();
        const count = this.pageCount;
        this.dots.hidden = count < 2;
        if (this.dots.children.length !== count) {
          this.dots.innerHTML = '';
          for (let i = 0; i < count; i++) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'cg-slider__dot';
            dot.setAttribute('aria-label', `Go to slide ${i + 1} of ${count}`);
            dot.addEventListener('click', () => this.go(i));
            this.dots.appendChild(dot);
          }
        }
        this.sync();
      }

      go(page) {
        const target = this.slides[Math.max(0, Math.min(this.pageCount - 1, page)) * this.perPage];
        if (target) this.track.scrollTo({ left: target.offsetLeft - this.slides[0].offsetLeft });
      }

      sync() {
        const page = this.page;
        if (this.prev) this.prev.disabled = page === 0;
        if (this.next) this.next.disabled = page >= this.pageCount - 1;
        if (this.dots) {
          Array.from(this.dots.children).forEach((dot, i) => dot.setAttribute('aria-current', String(i === page)));
        }
      }
    }
  );
}

// "Save my spot" buttons rendered with popup: true carry data-cg-popup. Point them at the
// page's popup form section (sections/popup-form.liquid) so assets/openPopupForm.js opens it.
// Without a popup on the page they keep their link.
(() => {
  const linkPopupButtons = () => {
    const popup = document.querySelector('[data-ghl-popup]');
    if (!popup) return;
    if (!popup.id) popup.id = 'CryoGamesPopupForm';
    document.querySelectorAll('[data-cg-popup]').forEach((button) => {
      button.setAttribute('data-ghl-modal-open', popup.id);
      button.setAttribute('aria-haspopup', 'dialog');
      button.removeAttribute('aria-disabled');
      if (!button.hasAttribute('href')) button.setAttribute('href', `#${popup.id}`);
    });
  };

  linkPopupButtons();
  document.addEventListener('shopify:section:load', linkPopupButtons);
})();

// The 5 days accordion (sections/cryo-games-days.liquid) shows every day open on mobile, as in
// the design. From 750px it is an exclusive accordion again (shared name) with the first day open.
(() => {
  const mobile = window.matchMedia('(max-width: 749px)');

  const syncDays = () => {
    document.querySelectorAll('.cg-days__list').forEach((list) => {
      list.querySelectorAll('.cg-days__item').forEach((item, index) => {
        if (mobile.matches) {
          // Drop the shared name first, or opening one day would close the others.
          if (item.hasAttribute('name')) {
            item.dataset.cgName = item.getAttribute('name');
            item.removeAttribute('name');
          }
          item.open = true;
        } else if (item.dataset.cgName) {
          item.setAttribute('name', item.dataset.cgName);
          delete item.dataset.cgName;
          item.open = index === 0;
        }
      });
    });
  };

  // Days can't be closed on mobile.
  document.addEventListener('click', (event) => {
    if (mobile.matches && event.target.closest('.cg-days__summary')) event.preventDefault();
  });

  syncDays();
  mobile.addEventListener('change', syncDays);
  document.addEventListener('shopify:section:load', syncDays);
})();
